import React from "react";
import type { Flavor, FlavorNote } from "@/types";
import { useI18n } from "@/i18n";

/** Roast level as five diamonds (the logo's mark), filled up to the level. */
export function RoastDiamonds({ level, withLabel = false }: { level?: number | null; withLabel?: boolean }) {
  const { t } = useI18n();
  if (!level) return null;
  return (
    <span className="roast" aria-label={t("flavor.roastAria", { level })} title={t(`flavor.roast${level}`)}>
      {[1, 2, 3, 4, 5].map((i) => (
        <i key={i} className={i <= level ? "on" : ""} />
      ))}
      {withLabel && <span className="roast__label">{t(`flavor.roast${level}`)}</span>}
    </span>
  );
}

const s = { fill: "none", stroke: "currentColor", strokeWidth: 1.4, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
/** Thin line icons for flavour descriptors, in the style of the pack label. */
export function NoteIcon({ note }: { note: FlavorNote }) {
  const paths: Record<FlavorNote, React.ReactNode> = {
    berry: (<><circle {...s} cx="9" cy="14" r="3.2" /><circle {...s} cx="15" cy="14" r="3.2" /><circle {...s} cx="12" cy="9.5" r="3.2" /><path {...s} d="M12 6.3V4" /></>),
    citrus: (<><circle {...s} cx="12" cy="12" r="7.5" /><path {...s} d="M12 4.5v15M4.5 12h15M6.7 6.7l10.6 10.6M17.3 6.7 6.7 17.3" /></>),
    stone_fruit: (<><path {...s} d="M12 6c-4.5 0-7 3-7 7s3 6.5 7 6.5 7-2.5 7-6.5-2.5-7-7-7Z" /><path {...s} d="M12 6c0-1.5.8-2.6 2.4-3" /></>),
    tropical: (<><path {...s} d="M12 20c-4 0-6-3-6-6.5S8.5 7 12 7s6 3 6 6.5S16 20 12 20Z" /><path {...s} d="M12 7 9 3.5M12 7l3-3.5M12 7V3" /></>),
    floral: (<><circle {...s} cx="12" cy="12" r="2.3" /><path {...s} d="M12 9.7C10 5 14 5 12 9.7M14.3 12c4.7-2 4.7 2 0 0M12 14.3c2 4.7-2 4.7 0 0M9.7 12c-4.7 2-4.7-2 0 0" /></>),
    chocolate: (<><rect {...s} x="6" y="4" width="12" height="16" rx="1.5" /><path {...s} d="M6 9.3h12M6 14.6h12M12 4v16" /></>),
    caramel: (<><path {...s} d="M5 9h14l-1.5 10h-11Z" /><path {...s} d="M8 9c0-3 1.8-5 4-5s4 2 4 5" /></>),
    nutty: (<><path {...s} d="M12 4c4 0 6.5 3.4 6.5 8S15.5 20 12 20s-6.5-3.4-6.5-8S8 4 12 4Z" /><path {...s} d="M12 4v16" /></>),
    spice: (<><path {...s} d="M12 3l1.8 5.4 5.7.2-4.5 3.5 1.6 5.4L12 14.3 7.4 17.5 9 12.1 4.5 8.6l5.7-.2Z" /></>),
    tea: (<><path {...s} d="M5 9h11v5a5 5 0 0 1-5 5h-1a5 5 0 0 1-5-5Z" /><path {...s} d="M16 10.5h1.5a2.3 2.3 0 0 1 0 4.6H16M9 4c1 1 1 2 0 3" /></>),
    wine: (<><path {...s} d="M8 3h8l-.5 5a3.5 3.5 0 0 1-7 0Z" /><path {...s} d="M12 11.5V20M9 20h6" /></>),
    honey: (<><path {...s} d="M12 3.5 18.5 7v7L12 17.5 5.5 14V7Z" /><path {...s} d="M12 17.5V21" /></>),
  };
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      {paths[note]}
    </svg>
  );
}

/** Flavour card: descriptors + four scales. Shown only when the admin filled it in. */
export function FlavorProfile({ flavor }: { flavor?: Flavor | null }) {
  const { t } = useI18n();
  if (!flavor) return null;
  const scales = (["acidity", "sweetness", "body", "bitterness"] as const).filter((k) => typeof flavor[k] === "number");
  if (!flavor.notes?.length && scales.length === 0) return null;
  return (
    <section className="flavor-card">
      <div className="section-heading" style={{ margin: "0 0 12px", fontSize: 19 }}>
        <span className="diamond" />
        {t("flavor.title")}
      </div>
      {!!flavor.notes?.length && (
        <div className="notes">
          {flavor.notes.map((n) => (
            <span key={n} className="note">
              <NoteIcon note={n} />
              {t(`flavor.notes.${n}`)}
            </span>
          ))}
        </div>
      )}
      <div className="flavor-scales">
        {scales.map((k) => (
          <React.Fragment key={k}>
            <span>{t(`flavor.${k}`)}</span>
            <div className="flavor-bar" role="meter" aria-valuemin={0} aria-valuemax={5} aria-valuenow={flavor[k]}>
              {[1, 2, 3, 4, 5].map((i) => (
                <span key={i} className={i <= (flavor[k] ?? 0) ? "on" : ""} />
              ))}
            </div>
          </React.Fragment>
        ))}
      </div>
    </section>
  );
}
